import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { Box, Stack, Typography, InputBase, IconButton } from '@mui/material'
import BoardList from '@components/Board/BoardList'
import BoardListTable from '@components/Board/BoardListTable'
import SearchIcon from '@mui/icons-material/Search'
import { useIsMobile } from '@/hooks/Responsive'
import { useParams } from 'react-router-dom'
import { fetchMyManagementDetail, fetchMyManagementDetailStatistics } from '@/api/mypage'

export function StudentToolbar({ search, onSearch, total, isMobile, }) {
  const [inputValue, setInputValue] = useState(search)

  const handleSubmit = () => {
    const keyword = inputValue.trim()
    onSearch(keyword)
  }

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      justifyContent="space-between"
      alignItems={isMobile ? 'flex-start' : 'center'}
      spacing={1}
      sx={{ mb: 1.5 }}
    >
      <Typography variant="subtitle1" fontWeight={600}>
        수강생 목록
      </Typography>

      {/* 검색창 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: (theme) => theme.shape.radius.md,
          px: 1.5,
          height: '48px',
          width: isMobile ? '100%' : '260px',
        }}
      >
        <InputBase
          placeholder="검색어를 입력해 주세요"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          sx={{
            flex: 1,
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit()
          }}
        />
        <IconButton
          onClick={handleSubmit}
          sx={{ color: 'grey.dark' }}
        >
          <SearchIcon />
        </IconButton>
      </Box>
    </Stack>
  )
}

export default function SummaryTab() {
  const { isMobile } = useIsMobile()
  const { id } = useParams()

  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({ field: 'no', direction: 'desc' })
  const [summaryData, setSummaryData] = useState(null)

  const handleSort = (field) => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  // Fetch summary statistics
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await fetchMyManagementDetailStatistics({ params: { eduLectureId: id } })
        setSummaryData(response.data)
      } catch (error) {
        console.error('Failed to fetch summary statistics:', error)
        setSummaryData(null)
      }
    }

    if (id) {
      fetchSummary()
    }
  }, [id])

  // Fetcher for student list
  const fetchStudents = useCallback(async ({ page, pageSize }) => {
    try {
      const params = {
        page: page,
        size: pageSize,
        keyword: search || undefined,
        sort: sort.field ? `${sort.field},${sort.direction}` : undefined,
        eduLectureId: id,
      }
      Object.keys(params).forEach((key) => params[key] === undefined && delete params[key])

      // Assuming `id` is a path parameter for the endpoint
      const response = await fetchMyManagementDetail({ params })
      
      return {
        items: response.data.content || [],
        total: response.data.totalElements || 0,
      }
    } catch (error) {
      console.error('Failed to fetch students:', error)
      return { items: [], total: 0 }
    }
  }, [id, search, sort])

  function SummaryRow({ summary }) {
    if (!summary) {
      return null // Or a loading skeleton
    }

    const items = [
      { label: '현재 수강생', value: summary.traineeCnt+'명' },//TRAINEE_CNT
      { label: '평균 진도율', value: summary.progressRate !== null ? summary.progressRate+'%':'0%' },//PROGRESS_RATE
      { label: '설문 참여율', value: '-' },//확인중
      { label: '수료 완료', value: summary.finishComCnt+'명('+summary.finishComCntRate+'%)' },//FINISH_COM_CNT 
    ]

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          flexDirection: isMobile ? 'column' : 'row',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: (theme) => theme.shape.radius.lg,
          overflow: 'hidden',
          mb: 7,
        }}
      >
        {items.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              position: 'relative',
              flex: 1,
              px: 4,
              py: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* 라벨 */}
            <Typography color="primary.main" fontWeight={600}>
              {item.label}
            </Typography>

            {/* 값 */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ pt: isMobile ? 0 : 4, textAlign: 'right' }}>
              {item.value}
            </Typography>

            {/* Divider */}
            {idx < items.length - 1 && (
              <Box sx={{ 
                position: 'absolute', 
                top: isMobile ? 'calc(100% + 8px)': '50%', 
                right: isMobile ? '24px' : '0', 
                width: isMobile ? 'calc(100% - 48px)' : '1px', 
                height: isMobile ? '1px' : '74px', 
                bgcolor: 'divider', 
                transform: isMobile ? 'none' : 'translateY(-50%)' 
              }} />
            )}
          </Box>
        ))}
      </Box>
    )
  }

  const renderProgressRate = (v, row) => {
      return (
        <Typography color="text.secondary" fontWeight={500}>{v}%</Typography>
        )
    }
  const studentColumns = useMemo(
    () => [
      { field: 'rowNumber', role: 'no', headerName: '번호', width: '70px', align: 'center', hideTitleInCard: true },
      // { field: 'instName', role: 'institution', headerName: '기관 - 학과', align: 'center' },
      { field: 'instName', role: 'institution', headerName: '기관/학과', align: 'center', render: (v) => v ?? '한국방송통신대학교', },
      // { field: 'instLowerName', role: 'department', headerName: '학과', align: 'center' },
      { field: 'memberName', role: 'name', headerName: '이름', width: '180px', align: 'center' },
      {
        field: 'status',
        headerName: '진행상태',
        align: 'center',
        width: '120px',
        render: (v, row) => (
          <Typography>{`${row?.session ?? 0}/${row?.totalSession ?? 0}`}</Typography>
        ),
      },
      { field: 'progressRate', headerName: '진도율', align: 'center', width: '90px', render: renderProgressRate },
      // { field: 'survey', role: 'survey', headerName: '설문진행', align: 'center', width: '90px' },
    ],
    []
  )

  const toolbarRender = useCallback(
    (ctx) => (
      <StudentToolbar
        search={ctx.search}
        total={ctx.total}
        isMobile={isMobile}
        onSearch={(v) => {
          ctx.setSearch(v)
          setSearch(v)
        }}
      />
    ),
    [isMobile]
  )

  return (
    <Box>
      {/* === 강좌 요약 === */}
      <SummaryRow summary={summaryData} />

      {/* === 수강생 목록 === */}
      <BoardList
        view="table"
        columns={studentColumns}
        fetcher={fetchStudents}
        getRowId={(row) => row.id}
        TableComponent={BoardListTable}
        initialPageSize={10}
        showPagination
        sort={sort}
        onSort={handleSort}
        toolbarRender={toolbarRender}
        showStudentDetails
      />
    </Box>
  )
}
